import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { TableHead } from '@/shared/ui/primitives'

export type SortDir = 'asc' | 'desc'

type SortableTableHeadProps<K extends string> = {
  sortKey: K
  activeKey: K
  direction: SortDir
  onToggle: (key: K) => void
  children: ReactNode
  className?: string
  label?: string
  rowSpan?: number
  colSpan?: number
  adornment?: ReactNode
}

export function SortableTableHead<K extends string>({
  sortKey,
  activeKey,
  direction,
  onToggle,
  children,
  className,
  label,
  rowSpan,
  colSpan,
  adornment,
}: SortableTableHeadProps<K>) {
  const active = activeKey === sortKey
  const ariaSort: 'ascending' | 'descending' | 'none' = active
    ? direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none'
  const a11yLabel = label ?? (typeof children === 'string' ? children : sortKey)
  const sortButton = (
    <button
      type="button"
      onClick={() => onToggle(sortKey)}
      className="flex cursor-pointer select-none items-center gap-1 hover:text-foreground"
      aria-label={`Sort by ${a11yLabel}`}
    >
      {children}
      {active ? (
        direction === 'asc' ? (
          <ArrowUp className="size-3" />
        ) : (
          <ArrowDown className="size-3" />
        )
      ) : (
        <ArrowUpDown className="size-3 text-muted-foreground" />
      )}
    </button>
  )
  return (
    <TableHead className={className} aria-sort={ariaSort} rowSpan={rowSpan} colSpan={colSpan}>
      {adornment ? (
        <span className="inline-flex items-center gap-1">
          {sortButton}
          {adornment}
        </span>
      ) : (
        sortButton
      )}
    </TableHead>
  )
}
